import { useState, useEffect, useRef, useCallback } from 'react';
import api from '../api';

const WS_BASE = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
  ? 'ws://127.0.0.1:8000/ws/logs'
  : 'wss://leadszone-api.onrender.com/ws/logs';

export function useScraperStatus() {
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [status, setStatus] = useState({ results_count: 0 });
  const [results, setResults] = useState([]);
  const [resultsLoading, setResultsLoading] = useState(true);
  const [logs, setLogs] = useState([]);
  const [progress, setProgress] = useState(0);
  const [nextBatchIn, setNextBatchIn] = useState(0);
  const ws = useRef(null);

  // WebSocket para logs em tempo real
  useEffect(() => {
    const connectWS = () => {
      ws.current = new WebSocket(WS_BASE);
      ws.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setLogs((prev) => [...prev.slice(-150), data]);
          if (data.progress != null) setProgress(data.progress);
          if (data.next_batch_in !== undefined) setNextBatchIn(data.next_batch_in);
          if (data.type === 'batch_complete') {
            window.dispatchEvent(new Event('leadszonemap-batch-complete'));
          }
        } catch {
          setLogs((prev) => [
            ...prev.slice(-150),
            { message: event.data, type: 'info', timestamp: '' },
          ]);
        }
      };
      ws.current.onclose = () => setTimeout(connectWS, 3000);
    };
    connectWS();
    return () => ws.current?.close();
  }, []);

  // Polling de status a cada 2s
  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        const res = await api.get('/status');
        setStatus(res.data);
        setIsRunning(res.data.is_running);
        setIsPaused(res.data.is_paused);
        // Expose payload for recovering UI state
        if (res.data.payload) {
          setStatus(prev => ({ ...prev, payload: res.data.payload, job_id: res.data.job_id }));
        }
      } catch {
        // servidor offline — silencioso
      }
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const syncResults = useCallback(async (isAdmin, forceRefresh = false) => {
    try {
      const endpoint = isAdmin ? '/results/all' : '/results';
      const url = forceRefresh ? `${endpoint}?refresh=1` : endpoint;
      const res = await api.get(url);
      setResults(Array.isArray(res.data) ? res.data : []);
    } catch {
      // silencioso no polling
    } finally {
      setResultsLoading(false);
    }
  }, []);

  // Polling de resultados a cada 4s
  useEffect(() => {
    // isAdmin é passado externamente via syncResults, então
    // o consumidor deve chamar syncResults(isAdmin) manualmente na primeira vez
    // e aqui iniciamos o intervalo com um placeholder — ver App.jsx
  }, []);

  // Countdown local do drip mode
  useEffect(() => {
    let timer;
    if (nextBatchIn > 0 && isRunning) {
      timer = setInterval(() => setNextBatchIn((p) => (p > 0 ? p - 1 : 0)), 1000);
    }
    return () => clearInterval(timer);
  }, [nextBatchIn, isRunning]);

  const startScraping = useCallback(async (payload) => {
    await api.post('/start', payload);
    setIsRunning(true);
  }, []);

  const stopScraping = useCallback(async () => {
    await api.post('/stop');
    setNextBatchIn(0);
    setIsPaused(false);
  }, []);

  const pauseScraping = useCallback(async () => {
    await api.post('/pause');
    setIsPaused(true);
  }, []);

  const resumeScraping = useCallback(async () => {
    await api.post('/resume');
    setIsPaused(false);
  }, []);

  const clearResults = useCallback(async () => {
    await api.post('/clear');
    setResults([]);
  }, []);

  const resetLogs = useCallback(() => {
    setLogs([]);
    setProgress(0);
    setNextBatchIn(0);
  }, []);

  return {
    isRunning,
    isPaused,
    status,
    results,
    setResults,
    resultsLoading,
    logs,
    progress,
    nextBatchIn,
    syncResults,
    startScraping,
    stopScraping,
    pauseScraping,
    resumeScraping,
    clearResults,
    resetLogs,
  };
}
